/**
 * FT-446 — PUSH-side shared-id winner gate (single source of truth).
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * The PUSH pre-flight pull-merge-push had the OPPOSITE tie-break to the safe
 * PULL gate (`resolvePullMergeWinner` in UniversalSyncBridge.js). PUSH used
 * `localWins = !localTs || localTs >= cloudTs`, so a LOCAL record with NO
 * timestamp (localTs coerces to 0) ALWAYS won and overwrote a NEWER cloud
 * copy — and an EQUAL timestamp also won. Maintenance completions are created
 * WITHOUT `updatedAt` (only `completedAt`), so a device holding a stale,
 * unsigned completion would push its no-timestamp copy over the cloud's SIGNED
 * copy → the sign-off was silently wiped (reproduced live: Medical Locker +
 * TEST ELECTRICAL reverted to pending after another device signed off Fire
 * Extinguisher). The offline OUTBOX drain was worse still — it did
 * `localItems.forEach(i => uniqueMap.set(i.id, i))`, an UNCONDITIONAL local
 * overwrite with NO timestamp check at all.
 *
 * This module is the ONE canonical PUSH tie-break. It is a dependency-free
 * leaf (only pure functions here — no React, no db, no bridge import) so BOTH
 * `src/sync/UniversalSyncBridge.js` AND `src/lib/outbox.js` can import it
 * without recreating the `UniversalSyncBridge → outbox → UniversalSyncBridge`
 * import cycle that FT-331 deliberately broke by extracting `extractItems.js`.
 * (Importing `resolvePullMergeWinner`/`toEpochMs` from the bridge into outbox
 * WOULD recreate that cycle — hence a standalone leaf with its own toEpochMs.)
 *
 * ── The semantics ─────────────────────────────────────────────────────────
 * Mirrors `resolvePullMergeWinner`: for a SHARED id, the LOCAL copy wins ONLY
 * when its timestamp is KNOWN and STRICTLY NEWER than cloud's. On BLANK /
 * EQUAL / OLDER local timestamp → CLOUD wins.
 *
 * ⭐ HARD CONSTRAINT (legitimate offline edit): a GENUINELY newer local edit
 * (a real offline change with a real, later `updatedAt`/`lastSaved`) STILL
 * WINS — `localHasTs && localTs > cloudTs` is true, so it is kept and pushed
 * to cloud. Only a blank / equal / older local is stopped from clobbering a
 * newer cloud. This is intentionally IDENTICAL to the pull gate so both
 * directions agree on exactly the case that used to bite.
 *
 * NOTE on the `> cloudTs` (strict) vs old `>= cloudTs`: on an EQUAL timestamp
 * we now keep CLOUD instead of LOCAL. That is safe: equal-timestamp copies are
 * the same logical write (idempotent re-push), so keeping either is
 * value-equivalent; keeping cloud additionally prevents a stale-but-equal
 * local from resurrecting an id the cloud has since advanced within the same ms
 * — matching the pull gate's proven behaviour.
 *
 * @module sync/resolvePushMergeWinner
 */

/**
 * Normalize an `updatedAt` / `lastSaved` value of unknown type to epoch ms.
 * Self-contained duplicate of UniversalSyncBridge.toEpochMs so this module
 * stays a dependency-free leaf (see file header). Kept byte-for-byte
 * behaviourally identical:
 *  - finite Number → passthrough (including 0, negatives)
 *  - NaN / Infinity / -Infinity → 0
 *  - ISO 8601 string → epoch ms via Date.parse
 *  - unparseable / empty string → 0
 *  - null / undefined / boolean / object / array → 0
 *
 * @param {unknown} v
 * @returns {number} epoch milliseconds (0 for invalid / non-parseable inputs)
 */
export const toEpochMs = (v) => {
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  if (typeof v === 'string' && v) {
    const parsed = Date.parse(v);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

/**
 * PUSH-side shared-id winner gate.
 *
 * @param {object|null|undefined} localItem  local item for this id (or nullish)
 * @param {object|null|undefined} cloudItem  cloud item for this id (or nullish)
 * @returns {{ winner: 'local'|'cloud', localTs:number, cloudTs:number,
 *             localHasTs:boolean, cloudHasTs:boolean, localWins:boolean }}
 */
export const resolvePushMergeWinner = (localItem, cloudItem) => {
  const localHasTs = !!(localItem && (localItem.updatedAt || localItem.lastSaved));
  const cloudHasTs = !!(cloudItem && (cloudItem.updatedAt || cloudItem.lastSaved));
  const localTs = localItem ? toEpochMs(localItem.updatedAt || localItem.lastSaved || 0) : 0;
  const cloudTs = cloudItem ? toEpochMs(cloudItem.updatedAt || cloudItem.lastSaved || 0) : 0;
  // LOCAL wins ONLY when its ts is known AND strictly newer than cloud.
  // Blank/equal/older local → CLOUD wins. Identical to resolvePullMergeWinner.
  const localWins = !!localItem && localHasTs && localTs > cloudTs;
  return { winner: localWins ? 'local' : 'cloud', localTs, cloudTs, localHasTs, cloudHasTs, localWins };
};

export default resolvePushMergeWinner;
